import * as React from "react"
import {
  Box,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Img,
  Link,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  VisuallyHidden,
} from "@chakra-ui/react"
import Head from "next/head"
import { HomeLayout } from "components/HomeLayout"
import splitbee from "@splitbee/web"
import { teammates } from "lib/data"
import { FaLinkedinIn, FaTwitter } from "react-icons/fa"
import { LinkButton } from "components/LinkButton"
import { LogoImage } from "./index"

splitbee.track("AboutUs")

export default function AboutUs() {
  return (
    <Box>
      <Head>
        <title>ROAMpool About</title>
      </Head>
      <Members />
    </Box>
  )
}

AboutUs.getLayout = (page: React.ReactNode) => <HomeLayout>{page}</HomeLayout>

export interface Member {
  name: string
  role: string
  description?: string
  image?: string
  twitter?: string
  linkedin?: string
  children?: React.ReactNode
}

export const Member = (props: Member) => {
  const { image, name, role, twitter, linkedin, children } = props
  return (
    <Flex direction="column" align="center" textAlign="center">
      <Box>
        {" "}
        <Img alt={name} w="40" h="40" rounded="full" objectFit="cover" src={image ? image : ""} />
      </Box>
      <Box mt="4">
        <Text fontWeight="bold" fontSize="lg">
          {name}
        </Text>
        <Text fontWeight="semibold" color={useColorModeValue("gray.500", "whiteAlpha.700")}>
          {role}
        </Text>
      </Box>
      <HStack mb="5" spacing="3">
        <Link isExternal color={useColorModeValue("blue.600", "blue.300")} href={linkedin}>
          <VisuallyHidden>{`${name}'s Linkedin page`}</VisuallyHidden>
          <FaLinkedinIn aria-hidden />
        </Link>
        <Link isExternal color={useColorModeValue("blue.600", "blue.300")} href={twitter}>
          <VisuallyHidden>{`${name}'s Twitter page`}</VisuallyHidden>
          <FaTwitter aria-hidden />
        </Link>
      </HStack>
      <Text
        mt="2"
        textAlign={"center"}
        color={useColorModeValue("gray.600", "whiteAlpha.900")}
        maxW={{ base: "unset", md: "20rem" }}
        minH={"10rem"}
      >
        {children}
      </Text>
    </Flex>
  )
}

function Members() {
  return (
    <Box as="section">
      <Box
        mx="auto"
        textAlign="center"
        maxW={{ base: "xl", md: "7xl" }}
        px={{ base: "6", md: "8" }}
        py={{ base: "12", md: "20" }}
      >
        <Heading size="2xl" letterSpacing="tight" mb="5">
          Why roam? What does the name "roam" mean?
        </Heading>
        <Text fontSize="xl" maxW="4xl" mx="auto">
          <Container maxW={"3xl"} py={{ base: 20, md: 26 }}>
            <Text fontSize={"3xl"} fontFamily={"Caveat"} transform={"rotate(-1deg)"} m={20} align={"center"}>
              Jason met Douby through RV travel group and we both found shared interests in discussing
              traveling, full-time RV lifestyle, and blockchain.
              <br />
              <br />
              We discussed blockchain and settled on working with Cardano. We think this blockchain has huge
              potential, for numerous reasons.
              <br />
              <br />
              To us, roam represents a lifestyle, freedom, and decentralization movement.
            </Text>
          </Container>
        </Text>
      </Box>

      <Box
        mx="auto"
        maxW={{ base: "xl", md: "7xl" }}
        px={{ base: "6", md: "8" }}
        py={{ base: "12", md: "20" }}
      >
        <Box textAlign="center">
          <Heading size="2xl" letterSpacing="tight" mb="5">
            Team
          </Heading>
          <Text fontSize="xl" maxW="4xl" mx="auto">
            Our team brings combined 18 years of experience in software engineering, data science, and
            management.
          </Text>
        </Box>
        <SimpleGrid mt="20" columns={{ base: 1, md: 2 }} spacingX="6" spacingY="16">
          {teammates.map((member, idx) => (
            <Member
              key={idx}
              role={member.role}
              image={member.image}
              name={member.name}
              twitter={member.twitter}
              linkedin={member.linkedin}
            >
              {member.description}
            </Member>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  )
}
